/**
 * PainelSuperAdminPage — as quatro abas do painel (F11).
 *
 * Réplica do super_admin.html: prefeituras (cadastro, edição, ativação),
 * usuários do sistema (papel, permissões, senha, exclusão), estatísticas e
 * a limpeza dos dados brutos da pesquisa de preços.
 *
 * Regras que o backend impõe e a tela apenas espelha: não se exclui a
 * própria conta nem um super admin; órgão é único por município; e
 * prefeitura inativa barra o login dos seus usuários.
 */

import {
  faCheck,
  faCity,
  faExclamationTriangle,
  faFileLines,
  faFolderOpen,
  faHospital,
  faPen,
  faPlus,
  faScaleBalanced,
  faSearchDollar,
  faSpinner,
  faToggleOff,
  faToggleOn,
  faTrashAlt,
  faUsers,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useParams } from 'react-router'
import {
  alternarPrefeitura,
  criarPrefeitura,
  editarPrefeitura,
  editarUsuario,
  excluirUsuario,
  limparAuditoriaDePrecos,
  type PainelSuperAdmin,
  painelSuperAdmin,
  type Prefeitura,
  type UsuarioSistema,
} from '../../api/superAdmin'
import { useToast } from '../../components/Toast'

const ROTULO_PERMISSAO: Record<string, string> = {
  processo_licitatorio: 'Processo Licitatório',
  auditoria: 'Auditoria',
  pesquisa_juridica: 'Pesquisa Jurídica',
  impugnacao: 'Impugnação',
  defesa: 'Defesa',
  auditoria_sus: 'Auditoria do SUS',
}

const CAMPO =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100'

function tamanho(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function PainelSuperAdminPage() {
  const { aba = 'prefeituras' } = useParams()
  const { data, isLoading } = useQuery({
    queryKey: ['super-admin', 'painel'],
    queryFn: painelSuperAdmin,
  })

  if (isLoading || !data) {
    return (
      <p className="p-8 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando o painel…
      </p>
    )
  }

  return (
    <div className="mx-auto max-w-7xl">
      <header className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="bg-gradient-to-r from-purple-700 via-purple-800 to-indigo-900 p-6 text-white">
          <h1 className="text-2xl font-bold">Painel do Super Admin</h1>
          <p className="mt-1 text-purple-200">
            Prefeituras, usuários e dados do sistema inteiro — sem filtro de
            tenant.
          </p>
        </div>
      </header>

      {aba === 'prefeituras' && <AbaPrefeituras painel={data} />}
      {aba === 'usuarios' && <AbaUsuarios painel={data} />}
      {aba === 'estatisticas' && <AbaEstatisticas painel={data} />}
      {aba === 'auditorias' && <AbaAuditoria painel={data} />}
    </div>
  )
}

// ── Prefeituras ─────────────────────────────────────────────────────────────

function AbaPrefeituras({ painel }: { painel: PainelSuperAdmin }) {
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const [emEdicao, setEmEdicao] = useState<Prefeitura | null>(null)
  const vazia = {
    municipio: '',
    estado: 'MA',
    orgao: '',
    razao_social: '',
    cnpj: '',
    endereco: '',
  }
  const [campos, setCampos] = useState(vazia)

  const recarregar = () =>
    clienteQuery.invalidateQueries({ queryKey: ['super-admin', 'painel'] })

  const salvar = useMutation({
    mutationFn: () =>
      emEdicao
        ? editarPrefeitura(emEdicao.id, campos)
        : criarPrefeitura(campos),
    onSuccess: () => {
      notificar('success', emEdicao ? 'Órgão atualizado.' : 'Órgão criado.')
      setEmEdicao(null)
      setCampos(vazia)
      void recarregar()
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  const alternar = useMutation({
    mutationFn: (id: number) => alternarPrefeitura(id),
    onSuccess: (r) => {
      notificar('success', r.ativa ? 'Prefeitura ativada.' : 'Prefeitura desativada.')
      void recarregar()
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  function editar(p: Prefeitura) {
    setEmEdicao(p)
    setCampos({
      municipio: p.municipio,
      estado: p.estado || 'MA',
      orgao: p.orgao,
      razao_social: p.razao_social,
      cnpj: p.cnpj,
      endereco: p.endereco,
    })
  }

  return (
    <>
      <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={emEdicao ? faPen : faPlus} className="text-purple-600" />
          {emEdicao
            ? `Editando ${emEdicao.municipio} — ${emEdicao.orgao}`
            : 'Novo órgão'}
        </h2>
        <form
          className="grid grid-cols-1 gap-4 md:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault()
            salvar.mutate()
          }}
        >
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Município <span className="text-red-500">*</span>
            <input
              id="municipio"
              required
              value={campos.municipio}
              onChange={(e) => setCampos({ ...campos, municipio: e.target.value })}
              className={`mt-1 ${CAMPO}`}
            />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Estado
            <select
              id="estado"
              value={campos.estado}
              onChange={(e) => setCampos({ ...campos, estado: e.target.value })}
              className={`mt-1 ${CAMPO}`}
            >
              {painel.estados.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Órgão / Secretaria
            <input
              id="orgao"
              placeholder="Administração Central"
              value={campos.orgao}
              onChange={(e) => setCampos({ ...campos, orgao: e.target.value })}
              className={`mt-1 ${CAMPO}`}
            />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
            CNPJ
            <input
              id="cnpj"
              value={campos.cnpj}
              onChange={(e) => setCampos({ ...campos, cnpj: e.target.value })}
              className={`mt-1 ${CAMPO}`}
            />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 md:col-span-2">
            Razão social
            <input
              id="razao_social"
              placeholder="Preenchida como «Órgão de Município» se vazia"
              value={campos.razao_social}
              onChange={(e) =>
                setCampos({ ...campos, razao_social: e.target.value })
              }
              className={`mt-1 ${CAMPO}`}
            />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 md:col-span-2">
            Endereço
            <input
              id="endereco"
              value={campos.endereco}
              onChange={(e) => setCampos({ ...campos, endereco: e.target.value })}
              className={`mt-1 ${CAMPO}`}
            />
          </label>
          <div className="flex gap-3 md:col-span-2">
            <button
              type="submit"
              disabled={salvar.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-purple-700 hover:to-indigo-700 disabled:opacity-60"
            >
              <FontAwesomeIcon
                icon={salvar.isPending ? faSpinner : faCheck}
                spin={salvar.isPending}
              />
              {emEdicao ? 'Salvar alterações' : 'Criar órgão'}
            </button>
            {emEdicao && (
              <button
                type="button"
                onClick={() => {
                  setEmEdicao(null)
                  setCampos(vazia)
                }}
                className="rounded-xl border border-slate-300 px-6 py-3 font-medium text-slate-600 dark:border-slate-600 dark:text-slate-300"
              >
                Cancelar
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faCity} className="text-purple-600" />
          Órgãos cadastrados
          <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
            {painel.prefeituras.length}
          </span>
        </h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-600">
                <th className="py-2 pr-4">Município</th>
                <th className="py-2 pr-4">Órgão</th>
                <th className="py-2 pr-4">CNPJ</th>
                <th className="py-2 pr-4">Usuários</th>
                <th className="py-2 pr-4">Situação</th>
                <th className="py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {painel.prefeituras.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-slate-100 dark:border-slate-700"
                >
                  <td className="py-2 pr-4 font-medium text-slate-800 dark:text-slate-100">
                    {p.municipio}
                    <span className="ml-1 text-xs text-slate-500">{p.estado}</span>
                  </td>
                  <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                    {p.orgao}
                  </td>
                  <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                    {p.cnpj || '—'}
                  </td>
                  <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                    {p.usuarios}
                  </td>
                  <td className="py-2 pr-4">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        p.ativa
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                      }`}
                    >
                      {p.ativa ? 'Ativa' : 'Inativa'}
                    </span>
                  </td>
                  <td className="py-2">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => editar(p)}
                        aria-label={`Editar ${p.municipio} ${p.orgao}`}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300"
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button
                        type="button"
                        onClick={() => alternar.mutate(p.id)}
                        aria-label={`${p.ativa ? 'Desativar' : 'Ativar'} ${p.municipio} ${p.orgao}`}
                        className={`rounded-lg border px-3 py-1.5 ${
                          p.ativa
                            ? 'border-red-200 text-red-600 hover:bg-red-50'
                            : 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'
                        }`}
                      >
                        <FontAwesomeIcon icon={p.ativa ? faToggleOn : faToggleOff} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  )
}

// ── Usuários ────────────────────────────────────────────────────────────────

function AbaUsuarios({ painel }: { painel: PainelSuperAdmin }) {
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const [emEdicao, setEmEdicao] = useState<UsuarioSistema | null>(null)
  const [busca, setBusca] = useState('')

  const recarregar = () =>
    clienteQuery.invalidateQueries({ queryKey: ['super-admin', 'painel'] })

  const excluir = useMutation({
    mutationFn: (id: number) => excluirUsuario(id),
    onSuccess: () => {
      notificar('success', 'Usuário excluído.')
      void recarregar()
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  const termo = busca.trim().toLowerCase()
  const visiveis = painel.usuarios.filter(
    (u) =>
      !termo ||
      u.nome.toLowerCase().includes(termo) ||
      u.email.toLowerCase().includes(termo) ||
      u.prefeitura.toLowerCase().includes(termo),
  )

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faUsers} className="text-purple-600" />
          Usuários do sistema
          <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
            {painel.usuarios.length}
          </span>
        </h2>
        <input
          type="search"
          aria-label="Buscar usuário"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por nome, e-mail ou prefeitura"
          className={`md:w-80 ${CAMPO}`}
        />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-600">
              <th className="py-2 pr-4">Usuário</th>
              <th className="py-2 pr-4">Prefeitura</th>
              <th className="py-2 pr-4">Papel</th>
              <th className="py-2 pr-4">Permissões</th>
              <th className="py-2">Ações</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 dark:border-slate-700">
                <td className="py-2 pr-4">
                  <span className="block font-medium text-slate-800 dark:text-slate-100">
                    {u.nome || '(sem nome)'}
                  </span>
                  <span className="block text-xs text-slate-500">{u.email}</span>
                </td>
                <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                  {u.prefeitura || '—'}
                </td>
                <td className="py-2 pr-4">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs ${
                      u.papel === 'super_admin'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                        : u.papel === 'admin'
                          ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                    }`}
                  >
                    {u.papel}
                  </span>
                </td>
                <td className="py-2 pr-4">
                  <div className="flex flex-wrap gap-1">
                    {u.permissoes.length === 0 ? (
                      <span className="text-xs text-slate-400">nenhuma</span>
                    ) : (
                      u.permissoes.map((p) => (
                        <span
                          key={p}
                          className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                        >
                          {ROTULO_PERMISSAO[p] ?? p}
                        </span>
                      ))
                    )}
                  </div>
                </td>
                <td className="py-2">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setEmEdicao(u)}
                      aria-label={`Editar ${u.email}`}
                      className="rounded-lg border border-slate-200 px-3 py-1.5 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300"
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                    <button
                      type="button"
                      disabled={u.papel === 'super_admin' || excluir.isPending}
                      onClick={() => {
                        if (confirm(`Excluir ${u.email}?`)) excluir.mutate(u.id)
                      }}
                      aria-label={`Excluir ${u.email}`}
                      className="rounded-lg border border-slate-200 px-3 py-1.5 text-red-600 hover:bg-red-50 disabled:opacity-30 dark:border-slate-600"
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {emEdicao && (
        <FormularioUsuario
          usuario={emEdicao}
          permissoes={painel.permissoes_disponiveis}
          aoFechar={() => setEmEdicao(null)}
          aoSalvar={() => {
            setEmEdicao(null)
            void recarregar()
          }}
        />
      )}
    </section>
  )
}

function FormularioUsuario({
  usuario,
  permissoes,
  aoFechar,
  aoSalvar,
}: {
  usuario: UsuarioSistema
  permissoes: string[]
  aoFechar: () => void
  aoSalvar: () => void
}) {
  const { notificar } = useToast()
  const [campos, setCampos] = useState({
    nome: usuario.nome,
    email: usuario.email,
    cpf: usuario.cpf,
    organizacao: usuario.organizacao,
    papel: (usuario.papel === 'admin' ? 'admin' : 'user') as 'user' | 'admin',
    senha: '',
    permissoes: [...usuario.permissoes],
  })

  const salvar = useMutation({
    mutationFn: () => editarUsuario(usuario.id, campos),
    onSuccess: () => {
      notificar('success', 'Usuário atualizado.')
      aoSalvar()
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-dark-card">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            Editar {usuario.email}
          </h3>
          <button type="button" onClick={aoFechar} aria-label="Fechar">
            <FontAwesomeIcon icon={faXmark} className="text-slate-400" />
          </button>
        </div>

        {usuario.papel === 'super_admin' && (
          <p className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5" />
            Super admin não é rebaixado por aqui — o papel continua o mesmo.
          </p>
        )}

        <form
          className="grid grid-cols-1 gap-4 md:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault()
            salvar.mutate()
          }}
        >
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Nome
            <input
              value={campos.nome}
              onChange={(e) => setCampos({ ...campos, nome: e.target.value })}
              className={`mt-1 ${CAMPO}`}
            />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
            E-mail <span className="text-red-500">*</span>
            <input
              required
              value={campos.email}
              onChange={(e) => setCampos({ ...campos, email: e.target.value })}
              className={`mt-1 ${CAMPO}`}
            />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
            CPF
            <input
              value={campos.cpf}
              onChange={(e) => setCampos({ ...campos, cpf: e.target.value })}
              className={`mt-1 ${CAMPO}`}
            />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Organização
            <input
              value={campos.organizacao}
              onChange={(e) =>
                setCampos({ ...campos, organizacao: e.target.value })
              }
              className={`mt-1 ${CAMPO}`}
            />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Papel
            <select
              value={campos.papel}
              onChange={(e) =>
                setCampos({ ...campos, papel: e.target.value as 'user' | 'admin' })
              }
              className={`mt-1 ${CAMPO}`}
            >
              <option value="user">Usuário</option>
              <option value="admin">Administrador</option>
            </select>
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Nova senha
            <input
              type="password"
              autoComplete="new-password"
              placeholder="Em branco mantém a atual"
              value={campos.senha}
              onChange={(e) => setCampos({ ...campos, senha: e.target.value })}
              className={`mt-1 ${CAMPO}`}
            />
          </label>

          <fieldset className="md:col-span-2">
            <legend className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
              Permissões
            </legend>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {permissoes.map((p) => (
                <label key={p} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={campos.permissoes.includes(p)}
                    onChange={(e) =>
                      setCampos({
                        ...campos,
                        permissoes: e.target.checked
                          ? [...campos.permissoes, p]
                          : campos.permissoes.filter((x) => x !== p),
                      })
                    }
                  />
                  <span className="text-slate-700 dark:text-slate-300">
                    {ROTULO_PERMISSAO[p] ?? p}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="flex justify-end gap-3 md:col-span-2">
            <button
              type="button"
              onClick={aoFechar}
              className="rounded-xl border border-slate-300 px-6 py-3 font-medium text-slate-600 dark:border-slate-600 dark:text-slate-300"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={salvar.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-3 font-semibold text-white shadow-lg disabled:opacity-60"
            >
              <FontAwesomeIcon
                icon={salvar.isPending ? faSpinner : faCheck}
                spin={salvar.isPending}
              />
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Estatísticas ────────────────────────────────────────────────────────────

function AbaEstatisticas({ painel }: { painel: PainelSuperAdmin }) {
  const e = painel.estatisticas
  const cartoes = [
    { rotulo: 'Prefeituras', valor: e.prefeituras, detalhe: `${e.prefeituras_ativas} ativas`, icone: faCity, cor: 'from-purple-500 to-indigo-500' },
    { rotulo: 'Usuários', valor: e.usuarios, detalhe: 'em todo o sistema', icone: faUsers, cor: 'from-blue-500 to-cyan-500' },
    { rotulo: 'Processos', valor: e.processos, detalhe: `${e.documentos} documentos gerados`, icone: faFolderOpen, cor: 'from-emerald-500 to-teal-500' },
    { rotulo: 'Auditorias', valor: e.auditorias, detalhe: 'de documentos', icone: faSearchDollar, cor: 'from-rose-500 to-orange-500' },
    { rotulo: 'Pesquisas jurídicas', valor: e.pesquisas_juridicas, detalhe: 'jurisprudência', icone: faScaleBalanced, cor: 'from-violet-500 to-purple-500' },
    { rotulo: 'Pareceres do SUS', valor: e.pareceres_sus, detalhe: 'emitidos', icone: faHospital, cor: 'from-teal-500 to-cyan-500' },
  ]
  return (
    <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {cartoes.map((c) => (
        <div
          key={c.rotulo}
          className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card"
        >
          <div className={`bg-gradient-to-r ${c.cor} p-4 text-white`}>
            <FontAwesomeIcon icon={c.icone} className="text-2xl" />
          </div>
          <div className="p-4">
            <p className="text-3xl font-bold text-slate-800 dark:text-slate-100">
              {c.valor}
            </p>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {c.rotulo}
            </p>
            <p className="text-xs text-slate-500">{c.detalhe}</p>
          </div>
        </div>
      ))}
    </section>
  )
}

// ── Auditoria de preços ─────────────────────────────────────────────────────

function AbaAuditoria({ painel }: { painel: PainelSuperAdmin }) {
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const [marcados, setMarcados] = useState<number[]>([])
  const auditoria = painel.auditoria

  const limpar = useMutation({
    mutationFn: () => limparAuditoriaDePrecos(marcados),
    onSuccess: (r) => {
      notificar(
        'success',
        `${r.arquivos} consulta(s) removidas · ${r.arquivos_no_disco} arquivo(s) ` +
          `apagados do disco (${tamanho(r.bytes)}).`,
      )
      setMarcados([])
      void clienteQuery.invalidateQueries({ queryKey: ['super-admin', 'painel'] })
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
        <FontAwesomeIcon icon={faFileLines} className="text-purple-600" />
        Auditoria da pesquisa de preços
      </h2>
      <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
        Cópia bruta das consultas de preço guardada para conferência (art. 23
        da Lei 14.133/2021). Limpar aqui não apaga o documento nem a pesquisa
        consolidada — só a cópia das consultas.
      </p>

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          ['Processos', String(auditoria.total_processos)],
          ['Arquivos', String(auditoria.total_arquivos)],
          ['Espaço ocupado', tamanho(auditoria.total_bytes)],
        ].map(([rotulo, valor]) => (
          <div
            key={rotulo}
            className="rounded-xl border border-slate-200 p-4 dark:border-slate-700"
          >
            <p className="text-xs uppercase text-slate-500">{rotulo}</p>
            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              {valor}
            </p>
          </div>
        ))}
      </div>

      {auditoria.processos.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-600">
          Nenhum dado bruto de pesquisa de preços armazenado.
        </p>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() =>
                setMarcados(
                  marcados.length === auditoria.processos.length
                    ? []
                    : auditoria.processos.map((p) => p.processo_id),
                )
              }
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 dark:border-slate-600 dark:text-slate-300"
            >
              {marcados.length === auditoria.processos.length
                ? 'Desmarcar todos'
                : 'Marcar todos'}
            </button>
            <button
              type="button"
              disabled={!marcados.length || limpar.isPending}
              onClick={() => {
                if (confirm(`Limpar os dados de ${marcados.length} processo(s)?`))
                  limpar.mutate()
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40"
            >
              <FontAwesomeIcon
                icon={limpar.isPending ? faSpinner : faTrashAlt}
                spin={limpar.isPending}
              />
              Limpar selecionados
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-600">
                  <th className="py-2 pr-4" />
                  <th className="py-2 pr-4">Processo</th>
                  <th className="py-2 pr-4">Município</th>
                  <th className="py-2 pr-4">Itens</th>
                  <th className="py-2 pr-4">Arquivos</th>
                  <th className="py-2 pr-4">Tamanho</th>
                  <th className="py-2 pr-4">Última consulta</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {auditoria.processos.map((p) => (
                  <tr
                    key={p.processo_id}
                    className="border-b border-slate-100 dark:border-slate-700"
                  >
                    <td className="py-2 pr-4">
                      <input
                        type="checkbox"
                        aria-label={`Selecionar processo ${p.processo_id}`}
                        checked={marcados.includes(p.processo_id)}
                        onChange={(e) =>
                          setMarcados(
                            e.target.checked
                              ? [...marcados, p.processo_id]
                              : marcados.filter((x) => x !== p.processo_id),
                          )
                        }
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <span className="block font-medium text-slate-800 dark:text-slate-100">
                        #{p.processo_id} {p.numero_processo && `· ${p.numero_processo}`}
                      </span>
                      <span className="block max-w-md truncate text-xs text-slate-500">
                        {p.objeto}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                      {p.municipio || '—'}
                    </td>
                    <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                      {p.itens}
                    </td>
                    <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                      {p.arquivos}
                    </td>
                    <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                      {tamanho(p.bytes)}
                    </td>
                    <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                      {p.ultima_consulta || '—'}
                    </td>
                    <td className="py-2">
                      <Link
                        to={`/super-admin/auditorias/${p.processo_id}`}
                        className="text-sm font-medium text-purple-600 hover:underline dark:text-purple-400"
                      >
                        Ver consultas
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  )
}
